/* Hallmark · component-discipline · panel-system
 * Rack summary / passport panel.
 * Colors: project CSS tokens only. No slate-* hardcoded.
 * Layout: section cards with consistent label/value pattern.
 */
import {
  Server, MapPin, User, BarChart3, Info,
  Ruler, Building2, Grid3X3, Hash, Plus, Bookmark, Trash2,
  Activity, Tag, Weight, Zap,
} from 'lucide-react';
import type { RackEquipmentItem as Equipment } from '../canvas/rackTypes';
import { rackDisplayTitle } from '../../utils/rackEdit.logic';
import { rackLabel } from '../../utils/rackName.logic';
import type { RackReservation, SpaceMetrics } from '../../utils/rackReservation.logic';
import {
  normalizeRackStatus,
  normalizeRackRole,
  RACK_STATUS_METAS,
  RACK_ROLE_METAS,
} from '../../utils/rackLifecycle.logic';

/* ── Types ───────────────────────────────────────────────────────────────── */

interface FreeBlock { start: number; size: number; }

interface RackConfig {
  name?: string;
  label?: string | null;
  type?: string;
  u_slots?: number;
  width_m?: number;
  depth_m?: number;
  max_weight_kg?: number;
  max_power_w?: number;
  tenant?: string;
  contact_email?: string;
  row_index?: number;
  rack_index?: number;
  [key: string]: any;
}

interface SysSettings {
  show_empty_slots?: boolean;
  show_tenant?: boolean;
  [key: string]: any;
}

interface RackSummaryPanelProps {
  currentRack: RackConfig | null;
  equipmentList: Equipment[];
  racksList: any[];
  dcInfoName?: string;
  actualCode: string;
  actualRackId: string | number | undefined;
  theme: 'dark' | 'light';
  sysSettings: SysSettings;
  slots: number;
  fillPct: number;
  usedU: number;
  currentWeight: number;
  currentPower: number;
  freeUBlocks: FreeBlock[];
  reservations?: RackReservation[];
  spaceMetrics?: SpaceMetrics;
  onAddEquipment: () => void;
  onAddReservation?: () => void;
  onReleaseReservation?: (res: RackReservation) => void;
  onSelectEquipment: (eq: Equipment) => void;
  onSlotClick: (u: number, mount: string) => void;
  onSelectRack?: (rackId: string) => void;
  DcMiniMap: React.ComponentType<{ racksList: any[]; currentRackId: string | number; onSelectRack?: (rackId: string) => void; className?: string }>;
}

/* ── TYPE_COLORS local (avoid circular import) ────────────────────────── */
const TYPE_COLORS: Record<string, string> = {
  server: '#00d2ff', switch: '#9d4edd', storage: '#ff9e00',
  firewall: '#ff007f', router: '#39ff14', pdu: '#ff003c',
  ups: '#ffb703', patch_panel: '#00f5d4',
};

/* ── Section card wrapper ─────────────────────────────────────────────── */
function SectionCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={className}
      style={{
        background: 'var(--bg-app)',
        border: '1px solid var(--border-color)',
        borderRadius: '2px',
        padding: '12px',
      }}
    >
      {children}
    </div>
  );
}

/* ── Section heading ──────────────────────────────────────────────────── */
function SectionHead({ icon: Icon, children }: { icon?: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 mb-3">
      {Icon && <Icon size={11} style={{ color: 'var(--accent)' }} aria-hidden />}
      <span
        className="text-[9px] uppercase tracking-[0.12em] font-semibold"
        style={{ color: 'var(--text-muted)' }}
      >
        {children}
      </span>
    </div>
  );
}

/* ── KV row ───────────────────────────────────────────────────────────── */
function KVRow({ icon: Icon, label, value, accent }: {
  icon?: React.ElementType;
  label: string;
  value: string | React.ReactNode;
  accent?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-1" style={{ borderBottom: '1px solid var(--border-color)' }}>
      <span className="flex items-center gap-1 text-[10px] shrink-0" style={{ color: 'var(--text-muted)' }}>
        {Icon && <Icon size={10} aria-hidden />}
        {label}
      </span>
      <span
        className="text-[11px] font-mono text-right min-w-0 truncate"
        style={{ color: accent || 'var(--text-secondary)' }}
      >
        {value}
      </span>
    </div>
  );
}

/* ── Space capacity 3-segment progress bar ────────────────────────────── */
function SpaceCapacityBar({ metrics, fillPct }: { metrics?: SpaceMetrics; fillPct: number }) {
  if (!metrics) {
    const color =
      fillPct >= 90 ? 'var(--status-critical)' :
      fillPct >= 70 ? 'var(--status-warning)' :
      'var(--status-normal)';
    return (
      <div
        style={{
          height: 4,
          background: 'var(--bg-input)',
          border: '1px solid var(--border-color)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        role="progressbar"
        aria-valuenow={fillPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Заполнение стойки ${fillPct}%`}
      >
        <div style={{ width: `${fillPct}%`, height: '100%', background: color, transition: 'width 500ms ease' }} />
      </div>
    );
  }

  return (
    <div
      style={{
        height: 6,
        background: 'var(--bg-input)',
        border: '1px solid var(--border-color)',
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
      }}
      role="progressbar"
      aria-label={`Занято ${metrics.occupiedPct}%, Резерв ${metrics.reservedPct}%, Свободно ${metrics.availablePct}%`}
    >
      <div
        style={{
          width: `${metrics.occupiedPct}%`,
          height: '100%',
          background: 'var(--color-accent-blue, #38bdf8)',
          transition: 'width 300ms ease',
        }}
        title={`Занято оборудованием: ${metrics.occupiedU}U (${metrics.occupiedPct}%)`}
      />
      <div
        style={{
          width: `${metrics.reservedPct}%`,
          height: '100%',
          background: 'repeating-linear-gradient(45deg, #f59e0b, #f59e0b 3px, #d97706 3px, #d97706 6px)',
          transition: 'width 300ms ease',
        }}
        title={`Зарезервировано: ${metrics.reservedU}U (${metrics.reservedPct}%)`}
      />
      <div
        style={{
          width: `${metrics.availablePct}%`,
          height: '100%',
          background: 'transparent',
        }}
        title={`Свободно: ${metrics.availableU}U (${metrics.availablePct}%)`}
      />
    </div>
  );
}

/* ── Main panel ───────────────────────────────────────────────────────── */
export function RackSummaryPanel({
  currentRack,
  equipmentList,
  racksList,
  dcInfoName,
  actualCode,
  actualRackId,
  sysSettings: _sysSettings,
  slots,
  fillPct,
  usedU,
  currentWeight,
  currentPower,
  reservations = [],
  spaceMetrics,
  onAddEquipment,
  onAddReservation,
  onReleaseReservation,
  onSelectEquipment,
  onSelectRack,
  DcMiniMap,
}: RackSummaryPanelProps) {

  const rackTypeName: Record<string, string> = {
    server_rack:  '19″ Серверная',
    network_rack: '19″ Сетевая',
    open_frame:   'Открытая рама',
    telecom:      'Телеком шкаф',
    pdu_frame:    'PDU Frame',
  };

  const mountedEq = equipmentList.filter(e => !e.is_zero_u);

  return (
    <div className="flex flex-col gap-3">

      {/* ── Header: rack identity + action ── */}
      <SectionCard>
        {/* Title row */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <h2 className="text-sm font-bold leading-tight truncate" style={{ color: 'var(--text-primary)' }}>
              {currentRack?.row_index != null && currentRack?.rack_index != null
                ? rackLabel({
                    label: currentRack.label,
                    row_index: currentRack.row_index,
                    rack_index: currentRack.rack_index,
                  })
                : rackDisplayTitle({
                    label: currentRack?.label ?? undefined,
                    id: actualRackId != null ? Number(actualRackId) : undefined,
                  })}
            </h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
              <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                <MapPin size={10} aria-hidden />
                {dcInfoName || actualCode.toUpperCase()}
                {currentRack?.row_index !== undefined ? ` · Ряд ${currentRack.row_index + 1}` : ''}
                {currentRack?.rack_index !== undefined ? ` · Поз. ${currentRack.rack_index + 1}` : ''}
              </span>
              {currentRack?.contact_email && (
                <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  <User size={10} aria-hidden />
                  {currentRack.contact_email}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {onAddReservation && (
              <button
                type="button"
                onClick={onAddReservation}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-sm border transition-colors border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                title="Зарезервировать юниты под проект / клиента"
              >
                <Bookmark size={11} />
                Резерв
              </button>
            )}

            <button
              onClick={onAddEquipment}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors"
              style={{ background: 'var(--accent)', color: '#fff' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent-hover)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--accent)' }}
            >
              <Plus size={12} aria-hidden />
              Монтировать
            </button>
          </div>
        </div>

        {/* Space Capacity Bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="flex items-center gap-1 text-[9px] uppercase tracking-[0.1em]" style={{ color: 'var(--text-muted)' }}>
              <BarChart3 size={10} aria-hidden />
              Ёмкость стойки
            </span>
            <span
              className="text-[11px] font-bold font-mono tabular-nums"
              style={{
                color: fillPct >= 90 ? 'var(--status-critical)' :
                       fillPct >= 70 ? 'var(--status-warning)' :
                       'var(--status-normal)',
              }}
            >
              {spaceMetrics ? spaceMetrics.utilizationPct : fillPct}% (
              {spaceMetrics ? `${spaceMetrics.occupiedU} зан / ${spaceMetrics.reservedU} рез` : `${usedU} / ${slots} U`}
              )
            </span>
          </div>
          <SpaceCapacityBar metrics={spaceMetrics} fillPct={fillPct} />
        </div>
      </SectionCard>

      {/* ── 3 metric chips ── */}
      <div className="grid grid-cols-3 gap-2">
        {[
          {
            label: 'Занято',
            value: `${spaceMetrics ? spaceMetrics.occupiedU : usedU} U`,
            color: 'var(--color-accent-blue, #38bdf8)',
          },
          {
            label: 'Резерв',
            value: `${spaceMetrics ? spaceMetrics.reservedU : 0} U`,
            color: '#f59e0b',
          },
          {
            label: 'Свободно',
            value: `${spaceMetrics ? spaceMetrics.availableU : slots - usedU} U`,
            color: 'var(--status-normal)',
          },
        ].map(m => (
          <div
            key={m.label}
            style={{
              background: 'var(--bg-app)',
              border: '1px solid var(--border-color)',
              borderRadius: 2,
              padding: '8px 10px',
            }}
          >
            <span className="block text-[9px] uppercase tracking-[0.1em] mb-1" style={{ color: 'var(--text-muted)' }}>
              {m.label}
            </span>
            <span className="block text-xs font-bold font-mono tabular-nums leading-tight" style={{ color: m.color }}>
              {m.value}
            </span>
          </div>
        ))}
      </div>

      {/* ── NetBox Slot Reservations List ── */}
      {reservations && reservations.length > 0 && (
        <SectionCard>
          <div className="flex items-center justify-between mb-2">
            <SectionHead icon={Bookmark}>Активные резервы ({reservations.length})</SectionHead>
          </div>
          <div className="flex flex-col gap-1.5">
            {reservations.map(res => {
              const range = res.u_height > 1
                ? `U${res.u_start}..U${res.u_start + res.u_height - 1}`
                : `U${res.u_start}`;
              return (
                <div
                  key={res.id}
                  className="flex items-center justify-between p-1.5 rounded border border-amber-500/30 bg-amber-500/5 text-[11px] font-mono"
                >
                  <div className="min-w-0 flex-1 truncate pr-2">
                    <div className="flex items-center gap-1.5">
                      <span className="px-1 py-0.2 rounded text-[8px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        {range} ({res.u_height}U)
                      </span>
                      <span className="font-bold text-amber-300 truncate">
                        {res.tenant}
                      </span>
                    </div>
                    {res.description && (
                      <p className="text-[9px] text-slate-400 truncate mt-0.5">
                        {res.description}
                      </p>
                    )}
                  </div>

                  {onReleaseReservation && (
                    <button
                      type="button"
                      onClick={() => onReleaseReservation(res)}
                      title="Освободить зарезервированные юниты"
                      className="p-1 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0"
                      aria-label="Освободить резерв"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* ── Passport section ── */}
      <SectionCard>
        <SectionHead icon={Info}>Паспорт стойки</SectionHead>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <KVRow
            icon={Grid3X3}
            label="Тип"
            value={rackTypeName[currentRack?.type || ''] || currentRack?.type || 'Стандартная'}
          />
          {(() => {
            const status = normalizeRackStatus(currentRack?.status);
            const role = normalizeRackRole(currentRack?.role, currentRack?.type);
            const statusMeta = RACK_STATUS_METAS[status];
            const roleMeta = RACK_ROLE_METAS[role];
            return (
              <>
                <KVRow
                  icon={Activity}
                  label="Статус"
                  value={
                    <span className="flex items-center gap-1.5">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ background: statusMeta.color }}
                      />
                      <span>{statusMeta.labelRu}</span>
                    </span>
                  }
                  accent={statusMeta.color}
                />
                <KVRow
                  icon={Tag}
                  label="Роль"
                  value={`${roleMeta.labelRu} [${roleMeta.shortCode}]`}
                  accent={roleMeta.color}
                />
              </>
            );
          })()}
          <KVRow
            icon={Ruler}
            label="Габариты"
            value={`${slots}U × ${currentRack?.width_m ? `${(currentRack.width_m * 100).toFixed(0)}см` : '60см'} × ${currentRack?.depth_m ? `${(currentRack.depth_m * 100).toFixed(0)}см` : '100см'}`}
          />
          <KVRow
            icon={Weight}
            label="Вес"
            value={`${Math.round(currentWeight)} / ${currentRack?.max_weight_kg || 1000} кг`}
            accent={currentRack?.max_weight_kg && currentWeight > currentRack.max_weight_kg ? 'var(--status-critical)' : undefined}
          />
          <KVRow
            icon={Zap}
            label="Мощность"
            value={`${Math.round(currentPower)} / ${currentRack?.max_power_w || 5000} Вт`}
            accent={currentRack?.max_power_w && currentPower > currentRack.max_power_w * 0.8 ? 'var(--status-warning)' : undefined}
          />
          <KVRow icon={Building2} label="Тенант" value={currentRack?.tenant || 'mine'} />
          {currentRack?.netbox_id != null && (
            <KVRow icon={Hash} label="NetBox ID" value={String(currentRack.netbox_id)} />
          )}
          {currentRack?.site && (
            <KVRow icon={MapPin} label="Площадка" value={currentRack.site} />
          )}
          {currentRack?.netbox_synced_at && (
            <KVRow icon={Activity} label="NetBox Синхр." value={new Date(currentRack.netbox_synced_at).toLocaleString('ru-RU')} />
          )}
        </div>
      </SectionCard>

      {/* ── Equipment list ── */}
      <SectionCard>
        <SectionHead icon={Server}>
          Оборудование ({mountedEq.length})
        </SectionHead>

        <div className="flex flex-col gap-1 max-h-64 overflow-y-auto pr-0.5">
          {mountedEq.length === 0 ? (
            <p className="text-center text-xs py-6" style={{ color: 'var(--text-muted)' }}>
              Стойка пуста. Нажмите «Монтировать».
            </p>
          ) : (
            mountedEq
              .sort((a, b) => b.u_position - a.u_position)
              .map(eq => (
                <button
                  key={eq.id}
                  onClick={() => onSelectEquipment(eq)}
                  className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-1 group"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    ['--tw-ring-color' as string]: 'var(--border-focus)',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-focus)'
                    ;(e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-color)'
                    ;(e.currentTarget as HTMLButtonElement).style.background = 'transparent'
                  }}
                >
                  {/* Type color bar */}
                  <div
                    className="w-1 h-4 rounded-full shrink-0"
                    style={{ background: TYPE_COLORS[eq.equipment_type] || 'var(--text-muted)' }}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                      {eq.name}
                    </p>
                    <p className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      U{eq.u_position}–U{eq.u_position + eq.u_height - 1}
                      {eq.power_w   ? ` · ${eq.power_w}W`   : ''}
                      {eq.weight_kg ? ` · ${eq.weight_kg}kg` : ''}
                    </p>
                  </div>
                  <span
                    className="text-[9px] font-mono shrink-0 px-1.5 py-0.5 rounded-sm"
                    style={{
                      background: 'var(--bg-input)',
                      color: 'var(--text-muted)',
                      border: '1px solid var(--border-color)',
                    }}
                  >
                    {eq.u_height}U
                  </span>
                </button>
              ))
          )}
        </div>
      </SectionCard>

      {/* ── Mini-map ── */}
      {racksList && racksList.length > 0 && (
        <SectionCard>
          <SectionHead icon={MapPin}>Позиция в ЦОД</SectionHead>
          <DcMiniMap
            racksList={racksList}
            currentRackId={actualRackId != null ? String(actualRackId) : ''}
            onSelectRack={onSelectRack}
          />
        </SectionCard>
      )}

    </div>
  );
}
